/* eslint-disable */
import { useEffect, useRef, useState } from 'react';
import { Card, Descriptions, Spin, Table, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

import callApi from '../../Api/config';
import { InvertFillButton } from '../../components/InvertFillButton';
import { PAYMENT_VOUCHER } from '../../constants/EntityName';
import { FEE_TYPES } from '../../constants/paymentVoucher';
import {
  AddWithdrawalForm,
  MemberWithdrawalTables,
  ReceiptsSection,
  SettlementSection,
} from '../../components/PaymentVoucher';
import AccountingStatusControl from '../../components/PaymentVoucher/AccountingStatusControl';
import {
  accountingStatusValue,
  canUpdateAccountingStatus,
  displayDate,
  displayText,
  downloadVoucherReport,
  money,
  saveAccountingStatus,
  statusTag,
} from '../../components/PaymentVoucher/helpers';
import { toastError, toastSuccess } from '../../service/Toast';

const { Text } = Typography;

const descLabelStyle = { fontWeight: 600 as const, color: '#637f92' };
const descContentStyle = { fontWeight: 600 as const, color: '#323232' };

const NAVY = '#004195';

const PaymentVoucherDetails = ({
  voucher: initialVoucher,
  voucherId,
  onVoucherChange,
  onListRefresh,
}: {
  voucher: any;
  voucherId?: string;
  onVoucherChange?: (voucher: any) => void;
  onListRefresh?: () => void;
}) => {
  const [voucher, setVoucher] = useState<any>(initialVoucher || {});
  const [loading, setLoading] = useState(true);
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleTableOpen, setSettleTableOpen] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [savingAccountingStatus, setSavingAccountingStatus] = useState(false);
  const saveSettlementActionRef = useRef<(() => Promise<void>) | null>(null);

  const applyUpdate = (next: any) => {
    setVoucher(next);
    onVoucherChange?.(next);
    onListRefresh?.();
  };

  const changeAccountingStatus = async (next: string) => {
    try {
      setSavingAccountingStatus(true);
      const result = await saveAccountingStatus(voucher?.id || voucherId, next);
      if (!result.ok) {
        toastError(result.message || 'Failed to update accounting status');
        return;
      }
      applyUpdate(result.voucher);
      toastSuccess(`Accounting status updated to "${result.voucher.accountingStatus}"`);
    } catch (error) {
      toastError('Failed to update accounting status');
    } finally {
      setSavingAccountingStatus(false);
    }
  };

  useEffect(() => {
    const id = voucherId || initialVoucher?.id || initialVoucher?.voucherId;
    if (!id) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await callApi(`${PAYMENT_VOUCHER}/${id}`, 'GET');
        if (cancelled) return;
        if (res?.status && res?.data?.data) {
          setVoucher(res.data.data);
        } else if (!initialVoucher?.voucherId) {
          toastError(res?.data?.message || 'Failed to load payment voucher');
        }
      } catch (error) {
        if (!cancelled) {
          toastError('Failed to load payment voucher');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherId]);

  const govtFee = Number(voucher?.feeTotals?.['Government Fee'] || 0);
  const courierFee = Number(voucher?.feeTotals?.['Courier Label'] || 0);
  const remaining = Number(voucher?.remainingAmount || 0);
  const canSettle = remaining > 0.004 && !voucher?.cancelled;
  const canRecordPayment = remaining > 0.004 && !voucher?.cancelled;

  const openSettlement = () => setSettleOpen(true);

  const saveTopSettlement = async () => {
    if (saveSettlementActionRef.current) {
      await saveSettlementActionRef.current();
    }
  };

  if (loading && !voucher?.voucherId) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin tip="Loading voucher..." />
      </div>
    );
  }

  const summaryCards = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gap: '12px',
        marginBottom: '20px',
      }}
    >
      <Card
        size="small"
        bodyStyle={{
          padding: '14px 16px',

          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        }}
        style={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #cbd5e1',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Received Amount
            </Text>
          </div>
          <div>
            <Text
              strong
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: NAVY,
                letterSpacing: '-0.02em',
              }}
            >
              {money(voucher.receivedAmount, voucher.currency)}
            </Text>
          </div>
        </div>
      </Card>

      <Card
        size="small"
        bodyStyle={{
          padding: '14px 16px',

          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        }}
        style={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #cbd5e1',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Govt. Fee
          </Text>
          <div>
            <Text
              strong
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#1e293b',
                letterSpacing: '-0.02em',
              }}
            >
              {money(govtFee, voucher.currency)}
            </Text>
          </div>
        </div>
      </Card>

      <Card
        size="small"
        bodyStyle={{
          padding: '14px 16px',

          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        }}
        style={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #cbd5e1',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Courier Label Fee
          </Text>
          <div>
            <Text
              strong
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#1e293b',
                letterSpacing: '-0.02em',
              }}
            >
              {money(courierFee, voucher.currency)}
            </Text>
          </div>
        </div>
      </Card>

      {/* <Card
        size="small"
        bodyStyle={{ padding: '16px 18px' }}
        style={{
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          boxShadow: 'none',
        }}
      >
        <Text
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            lineHeight: 1.2,
            display: 'block',
            marginBottom: '8px',
          }}
        >
          Withdrawn Amount
        </Text>
        <div>
          <Text
            strong
            style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#1e293b',
              letterSpacing: '-0.01em',
            }}
          >
            {money(voucher.withdrawnAmount, voucher.currency)}
          </Text>
        </div>
      </Card> */}
      <Card
        size="small"
        bodyStyle={{
          padding: '14px 16px',

          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        }}
        style={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #86efac',
          boxShadow: '0 1px 3px rgba(22, 163, 74, 0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#166534',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Remaining Amount
          </Text>
          <div>
            <Text
              strong
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#15803d',
                letterSpacing: '-0.02em',
              }}
            >
              {money(voucher.remainingAmount, voucher.currency)}
            </Text>
          </div>
        </div>
      </Card>

      <Card
        size="small"
        bodyStyle={{
          padding: '14px 16px',

          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        }}
        style={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #cbd5e1',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Settled Amount
          </Text>
          <div>
            <Text
              strong
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#1e293b',
                letterSpacing: '-0.02em',
              }}
            >
              {money(voucher.settledAmount, voucher.currency)}
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <div style={{ padding: '20px 24px 32px', position: 'relative' }}>
      {loading && (
        <div style={{ position: 'absolute', top: 24, right: 28, zIndex: 1 }}>
          <Spin size="small" />
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '16px 20px',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          marginBottom: '20px',
          gap: '20px',
          flexWrap: 'wrap',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '18px',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Text
                style={{
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#0f172a',
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  margin: 0,
                }}
              >
                {voucher.voucherId}
              </Text>
              {statusTag(voucher.status)}
               <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <DownloadOutlined onClick={() => downloadVoucherReport(voucher)}/>
              
            </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Text
                  style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#64748b',
                    lineHeight: 1.2,
                  }}
                >
                  Order ID:
                </Text>
                <Text
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: NAVY,
                    lineHeight: 1.2,
                  }}
                >
                  {displayText(voucher.displayOrderId || voucher.orderId)}
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Text
                  style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#64748b',
                    lineHeight: 1.2,
                  }}
                >
                  Service Name:
                </Text>
                <Text
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: NAVY,
                    lineHeight: 1.2,
                  }}
                >
                  {displayText(voucher.serviceName || voucher.service?.name)}
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Text
                  style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#64748b',
                    lineHeight: 1.2,
                  }}
                >
                  Package Name:
                </Text>
                <Text
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: NAVY,
                    lineHeight: 1.2,
                  }}
                >
                  {displayText(voucher.packageName || voucher.package?.name)}
                </Text>
              </div>
             
              {/* <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Text
                  style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#64748b',
                    lineHeight: 1.2,
                  }}
                >
                  Package Name:
                </Text>
                <Text
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: NAVY,
                    lineHeight: 1.2,
                  }}
                >
                  {displayText(voucher.packageName || voucher.package?.name)}
                </Text>
              </div> */}
              {/* {voucher.customerName && (
                <>
                  <Text style={{ color: '#cbd5e1', fontSize: '14px' }}>·</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Text
                      style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#64748b',
                        lineHeight: 1.2,
                      }}
                    >
                      Customer:
                    </Text>
                    <Text
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#1e293b',
                        lineHeight: 1.2,
                      }}
                    >
                      {voucher.customerName}
                    </Text>
                  </div>
                </>
              )} */}
            </div>
          </div>
        </div>

          {/* Editable by Accountant / Super Admin only. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#64748b',
                    lineHeight: 1.2,
                  }}
                >
                  Accounting Status:
                </Text>
                <AccountingStatusControl
                  value={accountingStatusValue(voucher)}
                  editable={canUpdateAccountingStatus()}
                  saving={savingAccountingStatus}
                  disabled={loading}
                  onChange={changeAccountingStatus}
                />
              </div>       
        {/* <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {canSettle && (
            <InvertFillButton
              variant={settleOpen ? 'red' : 'red'}
              size="sm"
              fontWeight="semibold"
              onClick={settleOpen ? saveTopSettlement : openSettlement}
            >
              {settleOpen ? 'Save Settlement' : 'Settle Remaining Amount'}
            </InvertFillButton>
          )}
          {canSettle && settleOpen && (
            <InvertFillButton
              variant="white"
              size="sm"
              fontWeight="semibold"
              onClick={() => setSettleOpen(false)}
            >
              Cancel
            </InvertFillButton>
          )}
         
           
         
        </div> */}
      </div>

      {settleOpen && canSettle && (
        <Card
          size="small"
          style={{
            marginBottom: '20px',
            borderRadius: '12px',
            border: '1px solid #bfdbfe',
            boxShadow: '0 1px 3px rgba(59, 130, 246, 0.1)',
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          }}
          bodyStyle={{ padding: 16 }}
        >
          <SettlementSection
            voucher={voucher}
            onSaved={updated => {
              setSettleOpen(false);
              applyUpdate(updated);
            }}
            open={settleOpen}
            onToggle={setSettleOpen}
            showTable={false}
            onSetSaveAction={save => {
              saveSettlementActionRef.current = save;
            }}
          />
        </Card>
      )}

      {summaryCards}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          border: '1px solid #e2e8f0',
          padding: '20px',
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* <Card
          size="small"
          title="A. Order Information"
          bodyStyle={{ padding: 16 }}
          style={{
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
          }}
          headStyle={{
            fontWeight: 700,
            fontSize: '15px',
            color: NAVY,
            letterSpacing: '0.02em',
            borderBottom: '1px solid #e2e8f0',
            padding: '14px 16px',
          }}
        >
          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, md: 2, lg: 3 }}
            labelStyle={descLabelStyle}
            contentStyle={descContentStyle}
          >
            <Descriptions.Item label="Order ID">
              {displayText(voucher.displayOrderId || voucher.orderId)}
            </Descriptions.Item>
            <Descriptions.Item label="Customer Name">
              {displayText(voucher.customerName)}
            </Descriptions.Item>
            <Descriptions.Item label="Order Date">
              {displayDate(voucher.orderDate)}
            </Descriptions.Item>
            <Descriptions.Item label="Total Members">
              {(voucher.members || []).length}
            </Descriptions.Item>
            <Descriptions.Item label="Service Name">
              {displayText(voucher.serviceName)}
            </Descriptions.Item>
          </Descriptions>
        </Card> */}

        <Card
          size="small"
          bodyStyle={{ padding: 16 }}
          style={{
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            background: '#fafbfc',
          }}
          headStyle={{
            fontWeight: 700,
            fontSize: '15px',
            color: NAVY,
            letterSpacing: '0.02em',
            borderBottom: '1px solid #e2e8f0',
            padding: '14px 16px',
          }}
        >
          <ReceiptsSection voucher={voucher} onSaved={applyUpdate} allowRecordReceipt={false} />
          {/* <Descriptions
            bordered
            size="small"
            column={{ xs: 1, md: 2, lg: 3 }}
            style={{ marginTop: 16 }}
            labelStyle={descLabelStyle}
            contentStyle={descContentStyle}
          >
            <Descriptions.Item label="Voucher ID">{voucher.voucherId}</Descriptions.Item>
            <Descriptions.Item label="Latest Payment Method">
              {voucher.paymentMethod}
              {voucher.bankAccount ? ` — ${voucher.bankAccount}` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Latest Transaction ID">
              {displayText(voucher.transactionId)}
            </Descriptions.Item>
            <Descriptions.Item label="Total Received Amount">
              {money(voucher.receivedAmount, voucher.currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Govt. Fee">
              {money(govtFee, voucher.currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Courier Label Fee">
              {money(courierFee, voucher.currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Latest Payment Date">
              {displayDate(voucher.receivedDate)}
            </Descriptions.Item>
            <Descriptions.Item label="Sender Name">
              {displayText(voucher.senderName)}
            </Descriptions.Item>
            <Descriptions.Item label="Recorded By">
              {displayText(voucher.createdBy)}
            </Descriptions.Item>
            <Descriptions.Item label="Created Date">
              {displayDate(voucher.createdAt, true)}
            </Descriptions.Item>
          </Descriptions> */}
        </Card>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',

            gap: '8px',
            flexWrap: 'wrap',
          }}
        >
          <Text
            strong
            style={{
              fontSize: '15px',
              fontWeight: 700,
              color: NAVY,
              letterSpacing: '0.02em',
            }}
          >
            Member-wise Record Payment ({(voucher.members || []).length})
          </Text>
        </div>
        {!canRecordPayment && !voucher?.cancelled && (
          <Text
            type="secondary"
            style={{
              display: 'block',
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: '8px',
              color: '#92400e',
              fontWeight: 500,
              fontSize: '13px',
            }}
          >
            Remaining amount is 0. Record Payment is disabled.
          </Text>
        )}
        {showWithdraw && canRecordPayment && (
          <AddWithdrawalForm
            voucher={voucher}
            onCancel={() => setShowWithdraw(false)}
            onSaved={updated => {
              applyUpdate(updated);
              setShowWithdraw(false);
            }}
          />
        )}
        <MemberWithdrawalTables voucher={voucher} />

        <Card
          size="small"
          style={{
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            background: '#fafbfc',
          }}
          bodyStyle={{ padding: 16 }}
        >
          <SettlementSection
            voucher={voucher}
            onSaved={updated => {
              setSettleTableOpen(false);
              applyUpdate(updated);
            }}
            open={settleTableOpen}
            onToggle={setSettleTableOpen}
          />
        </Card>

        {/* <div
          style={{
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}
        >
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={voucher.history || []}
            locale={{ emptyText: 'No tracking history yet.' }}
            columns={[
              {
                title: 'Date & Time',
                dataIndex: 'createdAt',
                render: (value: any) => displayDate(value, true),
              },
              { title: 'Action', dataIndex: 'action' },
              {
                title: 'Member',
                dataIndex: 'memberName',
                render: (value: any) => value || '—',
              },
              {
                title: 'Fee Type',
                dataIndex: 'feeType',
                render: (value: any) => value || '—',
              },
              {
                title: 'Amount',
                dataIndex: 'amount',
                render: (value: any) => (value != null ? money(value, voucher.currency) : '—'),
              },
              {
                title: 'Performed By',
                dataIndex: 'performedBy',
                render: (value: any) => value || '—',
              },
            ]}
          />
        </div> */}
      </div>
      <div
        style={{
          marginTop: '20px',
          padding: '16px',
          borderRadius: '8px',
          background: 'rgba(0,48,135,0.03)',
          border: '1px solid rgba(0,48,135,0.1)',
        }}
      >
        <div
          style={{
            marginBottom: '12px',
            fontSize: '15px',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            color: NAVY,
            paddingBottom: '8px',
            borderBottom: '1px solid rgba(0,48,135,0.15)',
          }}
        >
          Fee Type Summary
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {[
            { label: 'Total Received Amount', value: voucher.receivedAmount },
            ...FEE_TYPES.filter(feeType => Number(voucher.feeTotals?.[feeType] || 0) > 0.004).map(
              feeType => ({
                label: feeType,
                value: voucher.feeTotals?.[feeType] || 0,
              })
            ),
            ...(Number(voucher.settledAmount || 0) > 0.004
              ? [{ label: 'Settled Amount', value: voucher.settledAmount }]
              : []),
            { label: 'Remaining Amount', value: voucher.remainingAmount },
          ].map((item, index, array) => (
            <div key={item.label}>
              {index > 0 && (
                <span
                  style={{
                    height: '1px',
                    width: '100%',
                    backgroundColor: '#E5E7EB',
                    display: 'block',
                    marginBottom: '6px',
                  }}
                />
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span
                  style={{
                    fontSize: '15px',
                    color: item.label === 'Remaining Amount' ? NAVY : '#1e293b',
                    fontWeight: item.label === 'Remaining Amount' ? 800 : 600,
                  }}
                >
                  {item.label}
                </span>
                <span
                  style={{
                    fontSize: '15px',
                    fontWeight: item.label === 'Remaining Amount' ? 800 : 600,
                    color: item.label === 'Remaining Amount' ? '#15803d' : '#1e293b',
                  }}
                >
                  {money(item.value, voucher.currency)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PaymentVoucherDetails;
